import { useEffect, useState } from 'react';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import { Bond, mensagemDeErro } from '../api';
import { useConciliarVinculo, useRegistrarMatriculaFolha } from '../hooks';
import { PessoasModal } from './PessoasModal';

interface Props {
  bond: Bond | null;
  mode: 'registrar' | 'conciliar';
  onClose: () => void;
  onSaved: (message: string) => void;
}

/**
 * Retorno da contabilidade. Registrar nunca sobrescreve: valor diferente
 * do já gravado vira pendência de conciliação, resolvida aqui com
 * justificativa (EX-GP-001-007).
 */
export function PayrollModal({ bond, mode, onClose, onSaved }: Props) {
  const registrar = useRegistrarMatriculaFolha();
  const conciliar = useConciliarVinculo();
  const [value, setValue] = useState('');
  const [justification, setJustification] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue(mode === 'conciliar' ? bond?.payrollRegistration ?? '' : '');
    setJustification('');
    setError(null);
  }, [bond, mode]);

  async function submit() {
    if (!bond) return;
    if (!value.trim()) return setError('Informe a matrícula da folha.');
    try {
      if (mode === 'conciliar') {
        if (!justification.trim()) return setError('Justificativa é obrigatória.');
        await conciliar.mutateAsync({ id: bond.id, value: value.trim(), justification: justification.trim() });
        onSaved('Divergência conciliada.');
      } else {
        const saved = await registrar.mutateAsync({ id: bond.id, value: value.trim() });
        onSaved(
          saved.reconciliationPending
            ? 'O valor diverge do já registrado: ficou pendente de conciliação.'
            : 'Matrícula da folha registrada.',
        );
      }
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível registrar a matrícula da folha.'));
    }
  }

  return (
    <PessoasModal
      open={bond !== null}
      title={mode === 'conciliar' ? 'Conciliar divergência da contabilidade' : 'Retorno da contabilidade'}
      description={
        mode === 'conciliar'
          ? `Valor atual: ${bond?.payrollRegistration ?? '—'}. Informe o valor correto; consulte o histórico para ver o valor recebido.`
          : 'A matrícula da folha não substitui a matrícula interna.'
      }
      submitLabel={mode === 'conciliar' ? 'Conciliar' : 'Registrar'}
      isSubmitting={registrar.isPending || conciliar.isPending}
      submitError={error}
      onClose={onClose}
      onSubmit={submit}
    >
      <TextInput label="Matrícula da folha" value={value} onChange={(e) => setValue(e.target.value)} />
      {mode === 'conciliar' && (
        <Textarea label="Justificativa" value={justification} onChange={(e) => setJustification(e.target.value)} />
      )}
    </PessoasModal>
  );
}
